import React, { useState, useEffect } from 'react';
import { Loader } from '@aws-amplify/ui-react';
import { signUp, confirmSignUp, signIn, signInWithRedirect, fetchAuthSession, resendSignUpCode } from 'aws-amplify/auth';
import { useDispatch, useStore } from 'react-redux';
import { useWhatsAppMode, setGlobalWhatsAppMode } from '../../../hooks/useWhatsAppMode';
import { updateUserInRedux } from '../../../utils/updateUserInRedux';
import { dispatchAccountSetupAndWait } from '../../../utils/accountSetupHelper';
import { pollTokenRefresh } from '../../../utils/tokenRefreshPoller';
import DividerWithText from '../shared/DividerWithText';
import GoogleSignInButton from '../shared/GoogleSignInButton';
import PasswordInput from '../../ui/PasswordInput';

export const WhatsAppSignUp = () => {
    const dispatch = useDispatch();
    const store = useStore();
    const [mode] = useWhatsAppMode();
    const [step, setStep] = useState('signup'); // 'signup' or 'verify'
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        agreeToTerms: false,
    });
    const [verificationCode, setVerificationCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);

    useEffect(() => {
        let timer;
        if (resendCooldown > 0) {
            timer = setTimeout(() => {
                setResendCooldown((prev) => prev - 1);
            }, 1000);
        }
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    if (mode !== 'signUp') return null;

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let cleanedValue = value;

        if (name === 'phone') {
            cleanedValue = value.replace(/[^\d+\-() ]/g, '');
        }

        setFormData(prev => ({
            ...prev,
            [name]: cleanedValue
        }));
        setError('');
    };

    const handleCheckboxChange = (e) => {
        setFormData(prev => ({
            ...prev,
            agreeToTerms: e.target.checked
        }));
    };

    const handleCreateAccount = async () => {
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.password || !formData.agreeToTerms) {
            setError('Please fill all fields and agree to terms');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address');
            return;
        }

        if (formData.password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }

        let formattedPhone = formData.phone.trim();
        if (formattedPhone && !formattedPhone.startsWith('+')) {
            if (formattedPhone.startsWith('0')) {
                formattedPhone = '+91' + formattedPhone.substring(1);
            } else {
                formattedPhone = '+91' + formattedPhone;
            }
        }

        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            const signUpResponse = await signUp({
                username: formData.email,
                password: formData.password,
                options: {
                    userAttributes: {
                        email: formData.email,
                        given_name: formData.firstName,
                        family_name: formData.lastName,
                        phone_number: formattedPhone,
                    },
                    clientMetadata: {
                        signup_context: 'WHATSAPP_SIGNUP',
                    },
                },
            });

            if (signUpResponse.userId) {
                setStep('verify');
            }
        } catch (err) {
            if (err.code === 'UsernameExistsException') {
                setError('This email is already registered');
            } else if (err.code === 'InvalidPasswordException') {
                setError('Password does not meet requirements');
            } else if (err.message) {
                setError(err.message);
            } else {
                setError('Failed to create account. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        if (!verificationCode.trim()) {
            setError('Please enter the verification code');
            return;
        }

        setLoading(true);
        setError('');
        setSuccessMessage('');

        try {
            await confirmSignUp({
                username: formData.email,
                confirmationCode: verificationCode,
            });

            await signIn({
                username: formData.email,
                password: formData.password
            });

            await updateUserInRedux(dispatch);

            try {
                const { tokens } = await fetchAuthSession();
                const companyId = tokens?.idToken?.payload?.['custom:companyId'];

                if (!companyId) {
                    await dispatchAccountSetupAndWait(dispatch, store.getState);
                    await pollTokenRefresh(20, 2000);
                    await updateUserInRedux(dispatch, true);
                }
            } catch (setupError) {
                console.error('Account setup failed:', setupError);
                setError('Account setup failed. Some features may not be available. Please try refreshing the page.');
            }
        } catch (err) {
            if (err.code === 'ExpiredCodeException') {
                setError('Verification code expired. Please sign up again.');
            } else if (err.code === 'CodeMismatchException') {
                setError('Invalid verification code. Please try again.');
            } else if (err.code === 'NotAuthorizedException') {
                setError('Sign in failed after verification. Please try signing in manually.');
                setGlobalWhatsAppMode('signIn');
                setTimeout(() => {
                    setGlobalWhatsAppMode('signIn');
                }, 2000);
            } else if (err.message) {
                setError(err.message);
            } else {
                setError('Failed to verify email. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendCode = async () => {
        if (resendCooldown > 0) return;
        setLoading(true);
        setError('');
        setSuccessMessage('');
        try {
            await resendSignUpCode({ username: formData.email });
            setSuccessMessage('A new verification code has been sent to your email.');
            setResendCooldown(60);
        } catch (err) {
            setError(err.message || 'Failed to resend code. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignUp = async () => {
        try {
            await signInWithRedirect({ provider: 'Google' });
        } catch (err) {
            if (err.message?.includes('scopes')) {
                setError('Please authorize all requested permissions for signup');
            } else {
                setError('Failed to sign up with Google');
            }
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '10px 12px',
        paddingLeft: '38px',
        borderRadius: '6px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        boxSizing: 'border-box',
        fontFamily: 'inherit',
        background: '#f9fafb',
        color: '#1f2937',
    };

    const labelStyle = {
        display: 'block',
        textAlign: 'left',
        fontSize: '14px',
        fontWeight: '500',
        marginBottom: '8px',
        color: '#374151'
    };

    if (step === 'signup') {
        return (
            <div style={{ width: '100%' }}>
                {error && (
                    <div style={{
                        marginBottom: '16px',
                        padding: '12px',
                        borderRadius: '6px',
                        background: '#fee2e2',
                        color: '#dc2626',
                        fontSize: '14px',
                        border: '1px solid #fca5a5'
                    }}>
                        {error}
                    </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                        <div>
                            <label style={labelStyle}>First Name</label>
                            <div style={{ position: 'relative' }}>
                                <span style={{ position: 'absolute', left: '12px', top: '10px', color: '#9ca3af' }}>👤</span>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    placeholder="John"
                                    style={inputStyle}
                                    disabled={loading}
                                />
                            </div>
                        </div>
                        <div>
                            <label style={labelStyle}>Last Name</label>
                            <div style={{ position: 'relative' }}>
                                <span style={{ position: 'absolute', left: '12px', top: '10px', color: '#9ca3af' }}>👤</span>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    placeholder="Doe"
                                    style={inputStyle}
                                    disabled={loading}
                                />
                            </div>
                        </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                        <label style={labelStyle}>Email Address</label>
                        <div style={{ position: 'relative' }}>
                            <span style={{ position: 'absolute', left: '12px', top: '10px', color: '#9ca3af' }}>✉️</span>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="you@company.com"
                                style={inputStyle}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                        <label style={labelStyle}>Phone Number</label>
                        <div style={{ position: 'relative' }}>
                            <span style={{ position: 'absolute', left: '12px', top: '10px', color: '#9ca3af' }}>📱</span>
                            <input
                                type="tel"
                                name="phone"
                                value={formData.phone}
                                onChange={handleInputChange}
                                placeholder="+91 98765 43210"
                                style={inputStyle}
                                disabled={loading}
                            />
                        </div>
                    </div>

                    <PasswordInput
                        label="Password"
                        id="signup-password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Create a strong password"
                        disabled={loading}
                        labelStyle={{ textAlign: 'left' }}
                    />

                    <div style={{ marginBottom: '16px', textAlign: 'left' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={formData.agreeToTerms}
                                onChange={handleCheckboxChange}
                                style={{ cursor: 'pointer' }}
                                disabled={loading}
                            />
                            <span style={{ fontSize: '14px', color: '#4b5563' }}>
                                I agree to the {" "}
                                <button onClick={(e) => { e.preventDefault(); window.open('https://www.prodyt.com/terms-of-service', '_blank'); }} style={{ background: 'none', border: 'none', color: '#0891b2', textDecoration: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 'inherit' }}>Terms of Service</button>
                                 {" "} and {" "}
                                 <button onClick={(e) => { e.preventDefault(); window.open('https://www.prodyt.com/privacy-policy', '_blank'); }} style={{ background: 'none', border: 'none', color: '#0891b2', textDecoration: 'none', cursor: 'pointer', padding: 0, fontFamily: 'inherit', fontSize: 'inherit' }}>Privacy Policy</button>
                            </span>
                        </label>
                    </div>

                    <button
                        onClick={handleCreateAccount}
                        disabled={loading}
                        style={{
                            width: '100%',
                            padding: '12px',
                            backgroundColor: loading ? '#9ca3af' : '#26C485',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '15px',
                            fontWeight: '600',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'background-color 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                    >
                        {loading ? (
                            <>
                                <Loader size="small" /> Creating Account...
                            </>
                        ) : (
                            'Create Account'
                        )}
                    </button>
                </div>

                <DividerWithText />

                <GoogleSignInButton text="Continue with Google" onClick={handleGoogleSignUp} />

                <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: '#4b5563' }}>
                    Already have an account?{' '}
                    <button
                        onClick={(e) => { e.preventDefault(); setGlobalWhatsAppMode('signIn'); }}
                        style={{ background: 'none', border: 'none', color: '#26c485', textDecoration: 'none', cursor: 'pointer', fontWeight: '600', padding: 0, fontFamily: 'inherit', fontSize: 'inherit' }}
                    >
                        Sign in here
                    </button>
                </div>
            </div>
        );
    }

    if (step === 'verify') {
        return (
            <div style={{ width: '100%' }}>
                <div style={{ marginBottom: '24px', padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                    <div style={{ fontSize: '14px', color: '#15803d', margin: '0' }}>
                        ✓ Account created successfully! A verification code has been sent to<br />
                        <strong>{formData.email}</strong>
                    </div>
                </div>

                {successMessage && (
                    <div style={{
                        marginBottom: '16px',
                        padding: '12px',
                        borderRadius: '6px',
                        background: '#f0fdf4',
                        color: '#15803d',
                        fontSize: '14px',
                        border: '1px solid #86efac'
                    }}>
                        {successMessage}
                    </div>
                )}

                {error && (
                    <div style={{
                        marginBottom: '16px',
                        padding: '12px',
                        borderRadius: '6px',
                        background: '#fee2e2',
                        color: '#dc2626',
                        fontSize: '14px',
                        border: '1px solid #fca5a5'
                    }}>
                        {error}
                    </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                    <label style={labelStyle}>Verification Code</label>
                    <div style={{ position: 'relative' }}>
                        <span style={{ position: 'absolute', left: '12px', top: '10px', color: '#9ca3af' }}>🔐</span>
                        <input
                            type="text"
                            value={verificationCode}
                            onChange={(e) => { setVerificationCode(e.target.value); setError(''); setSuccessMessage(''); }}
                            placeholder="Enter 6-digit code"
                            maxLength="6"
                            style={{ ...inputStyle, textAlign: 'center', fontSize: '18px', letterSpacing: '2px' }}
                            disabled={loading}
                        />
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                        Check your email for the verification code
                    </div>
                </div>

                <button
                    onClick={handleVerifyCode}
                    disabled={loading}
                    style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: loading ? '#9ca3af' : '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '15px',
                        fontWeight: '600',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        transition: 'background-color 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                    }}
                >
                    {loading ? (
                        <>
                            <Loader size="small" /> Verifying...
                        </>
                    ) : (
                        'Verify Email'
                    )}
                </button>

                <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: '#4b5563' }}>
                    Didn't receive code?{' '}
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            if (resendCooldown === 0) handleResendCode();
                        }}
                        disabled={resendCooldown > 0}
                        style={{ 
                            background: 'none', 
                            border: 'none', 
                            color: resendCooldown > 0 ? '#9ca3af' : '#0891b2', 
                            textDecoration: 'none', 
                            cursor: resendCooldown > 0 ? 'not-allowed' : 'pointer', 
                            fontWeight: '600', 
                            padding: 0, 
                            fontFamily: 'inherit', 
                            fontSize: 'inherit' 
                        }}
                    >
                        {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                    </button>
                </div>
            </div>
        );
    }
};

export default WhatsAppSignUp;
